'use client';

import {
  Button,
  Typography,
  TypographyMods,
  TypographySizes,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';

import { FormField } from '~components/Forms/FormField';
import { FormInput } from '~components/Forms/FormInput';
import { FormInputMods } from '~components/Forms/FormInput/types';
import { NextImage } from '~components/NextImage/NextImage';
import { NextImageMods } from '~components/NextImage/NextImage.types';
import { PreloaderOverlay } from '~components/PreloaderOverlay/PreloaderOverlay';
import { Section } from '~components/Section/Section';
import { Validation } from '~helpers/forms/Validation';
import { useForm, UseFormField } from '~hooks/useForm';
import ReplayIcon from '~public/static/images/icons/Arrow_replay.svg';
import { BlogApi } from '~services/BlogApi';

import styles from './SectionSubscribe.module.scss';
import {
  SectionSubscribeMods,
  SectionSubscribeProps,
} from './SectionSubscribe.types';

export function SectionSubscribe({
  title,
  text,
  image,
  button,
  mailoutId,
  blockMods,
  ...sectionProps
}: SectionSubscribeProps) {
  const formFields: Record<string, UseFormField> = {
    email: {
      value: '',
      validate: Validation.required(Validation.email()),
    },
  };

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    status,
    setStatus,
    resetForm,
    isSubmitting,
    touched,
  } = useForm({
    fields: formFields,
    submit(data) {
      // const utm = getUtmFromUrl();

      BlogApi.subscribe({
        email: data.email,
        mailoutId: mailoutId,
      })
        .then(() => {
          // TODO: добавить цели ???
          // if (goals?.ym) {
          //   Goals.ym(goals.ym);
          // }

          // if (goals?.ga) {
          //   Goals.gtag4(goals.ga);
          // }
          //
          setStatus({
            success: {
              title: 'Спасибо!',
              message: `Вы подписались на нашу рассылку.  Теперь вам
              необходимо подтвердить вашу подписку. На ваш
              email ${data.email} отправлено письмо
              с подтверждением`,
            },
          });
        })
        .catch((error) => {
          console.error(error);
          setStatus({
            error: {
              title: 'Ошибка!',
              message:
                'Произошла ошибка из-за ввода некорректных данных. \n' +
                'Пожалуйста, заполните форму повторно',
            },
          });
        });
    },
  });

  function handleResetClick() {
    return resetForm();
  }

  return (
    <Section
      className={classNames(
        styles.container,
        blockMods?.map((mod) => styles[mod]),
      )}
      {...sectionProps}
    >
      {status.success && (
        <div className={styles.content}>
          <Typography
            tagName={TypographySizes.h2}
            mods={[TypographyMods.titleXl]}
            className={styles.title}
          >
            {status.success.title}
          </Typography>
          <Typography
            tagName={TypographySizes.p}
            mods={[TypographyMods.bodyM]}
            className={styles.text}
          >
            {status.success.message}
          </Typography>
          {image && (
            <NextImage
              className={classNames(styles.image, styles.isMobile)}
              mods={[NextImageMods.FitContain]}
              {...image}
            />
          )}
          <Button
            className={classNames(styles.button, styles.buttonReset)}
            mod="secondary"
            onClick={handleResetClick}
            size="m"
            type="reset"
          >
            <ReplayIcon className={styles.buttonIcon} />
            Заполнить повторно
          </Button>
        </div>
      )}

      {status.error && (
        <div className={styles.content}>
          <Typography
            tagName={TypographySizes.h2}
            mods={
              blockMods?.includes(SectionSubscribeMods.Small)
                ? [TypographyMods.titleL]
                : [TypographyMods.titleXl]
            }
            className={styles.title}
          >
            {status.error.title}
          </Typography>
          <Typography
            tagName={TypographySizes.p}
            mods={[TypographyMods.bodyM]}
            className={styles.text}
          >
            {status.error.message}
          </Typography>
          {image && (
            <NextImage
              className={classNames(styles.image, styles.isMobile)}
              mods={[NextImageMods.FitContain]}
              {...image}
            />
          )}
          <Button
            className={classNames(styles.button, styles.buttonReset)}
            mod="secondary"
            onClick={handleResetClick}
            size="m"
            type="reset"
          >
            <ReplayIcon className={styles.buttonIcon} />
            Заполнить повторно
          </Button>
        </div>
      )}

      {!status.success && !status.error && (
        <div className={styles.content}>
          <PreloaderOverlay active={isSubmitting} />
          <Typography
            tagName={TypographySizes.h2}
            mods={
              blockMods?.includes(SectionSubscribeMods.Small)
                ? [TypographyMods.titleL]
                : [TypographyMods.titleXl]
            }
            className={styles.title}
          >
            {title}
          </Typography>
          <Typography
            tagName={TypographySizes.p}
            mods={[TypographyMods.bodyM]}
            className={styles.text}
          >
            {text}
          </Typography>
          {image && (
            <NextImage
              className={classNames(styles.image, styles.isMobile)}
              mods={[NextImageMods.FitContain]}
              {...image}
            />
          )}
          <form onSubmit={handleSubmit} className={styles.form}>
            <FormField
              className={styles.formField}
              errors={touched.email && errors.email}
              label="Введите email"
            >
              <FormInput
                name="email"
                type="email"
                value={values.email}
                isError={Boolean(errors.email && touched.email)}
                onBlur={handleBlur}
                onChange={handleChange}
                mods={[FormInputMods.Lg]}
              />
            </FormField>
            {button && (
              <Button
                className={styles.button}
                text={button.text}
                type="submit"
                mod="tertiary"
                size="m"
                dataTest="sectionSubscribe.button"
              />
            )}
          </form>
        </div>
      )}

      {image && (
        <div className={styles.media}>
          <NextImage
            className={styles.image}
            mods={[NextImageMods.FitContain]}
            {...image}
          />
        </div>
      )}
    </Section>
  );
}
